import type {
    AnimatedIconPlayableAnimation,
    AnimatedIconMotionSpec,
    AnimatedIconEasings,
    AnimatedIconMotion,
    AnimatedIconTime,
    AnimatedIconSpec,
    AnimatedIconTag
} from "./type";
import {
    INTERACTION_STATE_SELECTOR
} from "../../helpers/interaction";
import {
    MOTION_EASINGS
} from "../../helpers/motion";

export const ANIMATED_ICON_REST_PROGRESS = 1;

export const ANIMATED_ICON_START_PROGRESS = 0;

export const ANIMATED_ICON_EASING_SAMPLE_COUNT = 24;

export const ANIMATED_ICON_HOST_ROLES: Array<string> = [
    "button",
    "link",
    "menuitem",
    "option",
    "tab"
];

export const ANIMATED_ICON_HOST_SELECTOR = [
    INTERACTION_STATE_SELECTOR,
    "a",
    "button",
    ...ANIMATED_ICON_HOST_ROLES.map((role) => `[role="${role}"]`)
].join(", ");

export const ANIMATED_ICON_DISABLED_SELECTOR = ":disabled, [aria-disabled=\"true\"]";

export const ANIMATED_ICON_EASINGS: AnimatedIconEasings = {
    linear: MOTION_EASINGS.linear,
    inOut: MOTION_EASINGS.inOut,
    out: MOTION_EASINGS.enter
};

export const ANIMATED_ICON_SPECS: Record<Exclude<AnimatedIconPlayableAnimation, "none">, AnimatedIconSpec> = {
    pulse: {
        keyframes: [
            {
                opacity: 1,
                offset: 0,
                scale: 1
            },
            {
                opacity: 0.55,
                scale: 0.92,
                offset: 0.5
            },
            {
                opacity: 1,
                offset: 1,
                scale: 1
            }
        ],
        duration: "breathe",
        isContinuous: true,
        durationScale: 1,
        easing: "inOut"
    },
    appear: {
        isContinuous: false,
        duration: "medium",
        keyframes: [
            {
                opacity: 0,
                scale: 0.6,
                offset: 0
            },
            {
                opacity: 1,
                offset: 1,
                scale: 1
            }
        ],
        durationScale: 1,
        easing: "out"
    },
    pop: {
        isContinuous: false,
        keyframes: [
            {
                offset: 0,
                scale: 1
            },
            {
                offset: 0.4,
                scale: 1.2
            },
            {
                offset: 1,
                scale: 1
            }
        ],
        duration: "medium",
        durationScale: 1,
        easing: "out"
    },
    spin: {
        keyframes: [
            {
                offset: 0,
                rotate: 0
            },
            {
                rotate: 360,
                offset: 1
            }
        ],
        isContinuous: true,
        duration: "spin",
        durationScale: 1,
        easing: "linear"
    },
    wiggle: {
        isContinuous: false,
        keyframes: [
            {
                offset: 0,
                rotate: 0
            },
            {
                rotate: -12,
                offset: 0.2
            },
            {
                offset: 0.4,
                rotate: 10
            },
            {
                offset: 0.6,
                rotate: -6
            },
            {
                offset: 0.8,
                rotate: 3
            },
            {
                offset: 1,
                rotate: 0
            }
        ],
        durationScale: 1.5,
        duration: "long",
        easing: "inOut"
    }
};

export const ANIMATED_ICON_TAGS: Array<AnimatedIconTag> = [
    "polyline",
    "ellipse",
    "polygon",
    "circle",
    "path",
    "line",
    "rect"
];

export const ANIMATED_ICON_VIEW_BOX = "0 0 24 24";

export const ANIMATED_ICON_CENTER: [number, number] = [
    12,
    12
];

export const ANIMATED_ICON_DASH_SCALE = 1.01;

export const ANIMATED_ICON_DASH_PADDING = 0.1;

export const ANIMATED_ICON_ANGLE_PRECISION = 4;

export const ANIMATED_ICON_STROKE_MIN_SCALE = 0.5;

export const ANIMATED_ICON_BLUR_FULL_SIZE = 40;

export const ANIMATED_ICON_SCALE_EPSILON = 0.001;

export const ANIMATED_ICON_TRANSITION_DURATION: AnimatedIconTime = [
    "medium",
    1
];

export const ANIMATED_ICON_DRAW_STAGGER: AnimatedIconTime = [
    "short",
    0.5
];

export const ANIMATED_ICON_DRAW_DURATION: AnimatedIconTime = [
    "long",
    1
];

export const ANIMATED_ICON_SIGNATURE_DURATION: AnimatedIconTime = [
    "long",
    1
];

export const ANIMATED_ICON_TRACE_DURATION: AnimatedIconTime = [
    "loop",
    1
];

export const ANIMATED_ICON_TRACE_STAGGER_SHARE = 0.4;

export const ANIMATED_ICON_TRACE_PHASES = {
    erase: 0.3,
    hold: 0.3,
    draw: 0.4
};

export const ANIMATED_ICON_STEP_OFFSET = 0.001;

export const ANIMATED_ICON_SCAN_STEPS = 8;

export const ANIMATED_ICON_MOTIONS: Record<AnimatedIconMotion, AnimatedIconMotionSpec> = {
    scan: {
        frames: Array.from({
            length: ANIMATED_ICON_SCAN_STEPS + 1
        }, (_, step) => ({
            ky: 1 - Math.cos(Math.PI * 2 * step / ANIMATED_ICON_SCAN_STEPS),
            kx: Math.sin(Math.PI * 2 * step / ANIMATED_ICON_SCAN_STEPS),
            offset: step / ANIMATED_ICON_SCAN_STEPS
        })),
        easing: "linear",
        defaults: {
            x: 1.5,
            y: 1.5
        }
    },
    draw: {
        frames: [
            {
                offset: 0,
                o: 0,
                d: 1
            },
            {
                offset: ANIMATED_ICON_STEP_OFFSET,
                o: 1,
                d: 1
            },
            {
                offset: 1,
                o: 1,
                d: 0
            }
        ]
    },
    drawpop: {
        frames: [
            {
                offset: 0,
                d: 1,
                o: 0,
                q: 0
            },
            {
                offset: ANIMATED_ICON_STEP_OFFSET,
                o: 1,
                d: 1
            },
            {
                offset: 0.6,
                d: 0,
                q: 0
            },
            {
                offset: 0.8,
                q: 1
            },
            {
                offset: 1,
                q: 0
            }
        ],
        defaults: {
            s: 1.2
        }
    },
    beat: {
        frames: [
            {
                offset: 0,
                q: 0
            },
            {
                offset: 0.14,
                q: 1
            },
            {
                offset: 0.28,
                q: 0
            },
            {
                offset: 0.42,
                q: 1
            },
            {
                offset: 0.56,
                q: 0
            },
            {
                offset: 1,
                q: 0
            }
        ],
        defaults: {
            s: 1.15
        }
    },
    drop: {
        frames: [
            {
                offset: 0,
                k: 1,
                o: 0
            },
            {
                offset: 0.15,
                o: 1
            },
            {
                offset: 0.5,
                k: 0
            },
            {
                offset: 0.7,
                k: 0.2
            },
            {
                offset: 0.85,
                k: 0
            },
            {
                offset: 1,
                k: 0
            }
        ]
    },
    flicker: {
        frames: [
            {
                offset: 0,
                o: 1
            },
            {
                offset: 0.1,
                o: 0.3
            },
            {
                offset: 0.2,
                o: 1
            },
            {
                offset: 0.3,
                o: 0.45
            },
            {
                offset: 0.45,
                o: 1
            },
            {
                offset: 0.6,
                o: 0.6
            },
            {
                offset: 0.8,
                o: 1
            },
            {
                offset: 1,
                o: 1
            }
        ],
        easing: "linear"
    },
    flip: {
        frames: [
            {
                offset: 0,
                q: 0
            },
            {
                offset: 0.35,
                q: 1
            },
            {
                offset: 0.65,
                q: 1
            },
            {
                offset: 1,
                q: 0
            }
        ],
        defaults: {
            sy: -1
        }
    },
    hop: {
        frames: [
            {
                offset: 0,
                k: 0
            },
            {
                offset: 0.3,
                k: 1
            },
            {
                offset: 0.55,
                k: 0
            },
            {
                offset: 0.75,
                k: 0.3
            },
            {
                offset: 1,
                k: 0
            }
        ]
    },
    shake: {
        frames: [
            {
                offset: 0,
                k: 0
            },
            {
                offset: 0.15,
                k: 1
            },
            {
                offset: 0.3,
                k: -1
            },
            {
                offset: 0.45,
                k: 0.7
            },
            {
                offset: 0.6,
                k: -0.5
            },
            {
                offset: 0.75,
                k: 0.25
            },
            {
                offset: 1,
                k: 0
            }
        ]
    },
    through: {
        frames: [
            {
                offset: 0,
                k: 0
            },
            {
                offset: 0.5,
                k: 1
            },
            {
                offset: 0.52,
                k: -1
            },
            {
                offset: 1,
                k: 0
            }
        ]
    },
    blink: {
        frames: [
            {
                offset: 0,
                q: 0
            },
            {
                offset: 0.4,
                q: 1
            },
            {
                offset: 1,
                q: 0
            }
        ],
        defaults: {
            sy: 0.1
        }
    },
    enter: {
        frames: [
            {
                offset: 0,
                k: 1,
                q: 1,
                o: 0
            },
            {
                offset: 0.4,
                o: 1
            },
            {
                offset: 1,
                k: 0,
                q: 0,
                o: 1
            }
        ]
    },
    lift: {
        frames: [
            {
                offset: 0,
                k: 0
            },
            {
                offset: 0.3,
                k: 1
            },
            {
                offset: 0.7,
                k: 1
            },
            {
                offset: 1,
                k: 0
            }
        ]
    },
    nudge: {
        frames: [
            {
                offset: 0,
                k: 0
            },
            {
                offset: 0.4,
                k: 1
            },
            {
                offset: 1,
                k: 0
            }
        ]
    },
    pop: {
        frames: [
            {
                offset: 0,
                k: 0,
                q: 0
            },
            {
                offset: 0.4,
                k: 1,
                q: 1
            },
            {
                offset: 1,
                k: 0,
                q: 0
            }
        ],
        defaults: {
            s: 1.2
        }
    },
    ring: {
        frames: [
            {
                offset: 0,
                k: 0
            },
            {
                offset: 0.1,
                k: 1
            },
            {
                offset: 0.2,
                k: -1
            },
            {
                offset: 0.3,
                k: 1
            },
            {
                offset: 0.4,
                k: -1
            },
            {
                offset: 0.5,
                k: 0.5
            },
            {
                offset: 0.6,
                k: 0
            },
            {
                offset: 1,
                k: 0
            }
        ]
    },
    squash: {
        frames: [
            {
                offset: 0,
                q: 0
            },
            {
                offset: 0.4,
                q: 1
            },
            {
                offset: 1,
                q: 0
            }
        ]
    },
    swing: {
        frames: [
            {
                offset: 0,
                k: 0
            },
            {
                offset: 0.2,
                k: 1
            },
            {
                offset: 0.4,
                k: -0.85
            },
            {
                offset: 0.6,
                k: 0.5
            },
            {
                offset: 0.8,
                k: -0.25
            },
            {
                offset: 1,
                k: 0
            }
        ]
    },
    twinkle: {
        frames: [
            {
                offset: 0,
                o: 1,
                u: 1
            },
            {
                offset: 0.3,
                u: 0.4,
                o: 0
            },
            {
                offset: 0.7,
                u: 1.25,
                o: 1
            },
            {
                offset: 1,
                o: 1,
                u: 1
            }
        ]
    },
    fade: {
        frames: [
            {
                offset: 0,
                o: 0
            },
            {
                offset: 1,
                o: 1
            }
        ]
    },
    grow: {
        frames: [
            {
                offset: 0,
                q: 1
            },
            {
                offset: 1,
                q: 0
            }
        ]
    },
    turn: {
        frames: [
            {
                offset: 0,
                k: 0
            },
            {
                offset: 1,
                k: 1
            }
        ]
    }
};
